import { ICommandBarItemProps } from '@fluentui/react';
import { IAppContext } from 'App/AppContext';
import { acceptIcon, deleteIcon } from 'globalStyles';
import { TFunction } from 'i18next';
import {
  addDateMonths,
  addDateTimeMinutes,
  fromApiDateOptional,
  fromApiDateRequired,
  fromApiDateTimeOptional,
  isValidDate,
  toApiDateRequired,
  toApiDateTimeRequired,
} from 'utils/datetime';

//Mirrors EvidenceRowKind in the API: what a row of the Azure evidence means for the outcome. The API decides it; the web
//shows it. A row's own state keeps the fact, so removing an acceptance shows the finding again without a new run.
export enum AzureRowKind {
  Finding = 0,
  Accepted = 1,
  Explained = 2, //a warning or an explanation: listed, but it does not make the check fail
  NotCounted = 3, //neither a failure nor a pass
  Ok = 4,
}

//Mirrors AcceptanceStaleReason in the API: why a stored acceptance no longer applied in a run.
export enum AcceptanceStaleReason {
  Expired = 0,
  NotFound = 1,
}

//The limits of the API's DatedAcceptanceValidator; the web enforces the same at entry.
export const MaxAcceptances = 50;
export const MaxAcceptanceReasonLength = 200;
export const MaxAcceptanceNameLength = 256;
export const MaxAcceptanceMonths = 6;

//One dated acceptance of a finding, as the check's config stores it (the API's AzureAcceptance).
export class AzureAcceptance {
  resourceId: string = ''; //the ARM id of the server, vault, web app, subscription or vault item, compared without case

  resourceName: string = ''; //the resource's name when it was accepted

  until: string = ''; //yyyy-MM-dd, the last day the acceptance applies

  reason: string = '';

  acceptedAtUtc: string = ''; //ISO, set by the web when the user accepts

  //the subject's state when it was accepted: the acceptance covers that state only, so a subject whose state changed
  //shows as the finding it is. Undefined for an acceptance made before the state was stored: it covers every state.
  state?: number;
}

//An acceptance applies to a subject with the same key and the state it was accepted in, as the API decides.
export const isAcceptanceForState = (acceptance: AzureAcceptance, state: number): boolean => {
  return acceptance.state === undefined || acceptance.state === state;
};

//The calendar day of a moment in UTC, as a local date at midnight. The API compares acceptances to the UTC day.
export const getUtcDay = (moment: Date): Date => {
  return fromApiDateRequired(toApiDateTimeRequired(moment));
};

//An end day as the API's DateOnly stores it (yyyy-MM-dd); fromApiDateOptional reads it back.
export const toAcceptanceDate = (day: Date): string => toApiDateRequired(day).substring(0, 10);

//The first and last day an acceptance made now may end on: today (UTC) up to 6 months later, as the API checks.
export const getAcceptanceLimits = (now: Date = new Date()): { minDate: Date; maxDate: Date } => {
  const minDate = getUtcDay(now);

  return { minDate, maxDate: addDateMonths(minDate, MaxAcceptanceMonths) };
};

//An acceptance applies up to and including its end day: compared as yyyy-MM-dd with today in UTC (the strings sort as
//the days do).
export const isAcceptanceActive = (acceptance: AzureAcceptance, now: Date = new Date()): boolean => {
  return acceptance.until >= toAcceptanceDate(getUtcDay(now));
};

//The rules of the API's validator, so Save never sends a list the API refuses.
export const isValidAcceptanceList = (
  acceptances: AzureAcceptance[],
  isValidKey: (key: string) => boolean,
  now: Date = new Date(),
): boolean => {
  if (acceptances.length > MaxAcceptances) return false;

  const keys = new Set<string>();
  for (const acceptance of acceptances) {
    if (!acceptance.resourceId || !isValidKey(acceptance.resourceId)) return false;
    const key = acceptance.resourceId.toLowerCase();
    if (keys.has(key)) return false;
    keys.add(key);

    const reason = acceptance.reason?.trim() ?? '';
    if (reason.length === 0 || reason.length > MaxAcceptanceReasonLength) return false;
    if ((acceptance.resourceName?.length ?? 0) > MaxAcceptanceNameLength) return false;

    const until = fromApiDateOptional(acceptance.until);
    const acceptedAt = fromApiDateTimeOptional(acceptance.acceptedAtUtc);
    if (!until || !isValidDate(until) || !acceptedAt || !isValidDate(acceptedAt)) return false;
    if (acceptedAt > addDateTimeMinutes(now, 5)) return false;

    const limits = getAcceptanceLimits(acceptedAt);
    if (until < limits.minDate || until > limits.maxDate) return false;
  }

  return true;
};

//Drops the acceptances a run reported as no longer applying (same key and end day) and the ones whose end day has
//passed since. Returns the list that stays and how many were dropped.
export const pruneAcceptances = (
  acceptances: AzureAcceptance[],
  noLongerApplies: AzureAcceptance[],
  now: Date = new Date(),
): { kept: AzureAcceptance[]; removed: number } => {
  const kept = acceptances.filter(
    (a) =>
      isAcceptanceActive(a, now) &&
      !noLongerApplies.some(
        (stale) => stale.resourceId.toLowerCase() === a.resourceId.toLowerCase() && stale.until === a.until,
      ),
  );

  return { kept, removed: acceptances.length - kept.length };
};

//The overlay rule: an acceptance made or removed in this session shows at once, before Save and before the next run.
//A finding with a live acceptance shows as accepted; an accepted row without one shows as the finding it is.
export const getDisplayKind = (evidenceKind: AzureRowKind, hasActiveAcceptance: boolean): AzureRowKind => {
  if (hasActiveAcceptance && (evidenceKind === AzureRowKind.Finding || evidenceKind === AzureRowKind.Accepted))
    return AzureRowKind.Accepted;
  if (!hasActiveAcceptance && evidenceKind === AzureRowKind.Accepted) return AzureRowKind.Finding;

  return evidenceKind;
};

//The API names who accepted by e-mail (from the config history); a list shows the person's name when it is known.
export const getAcceptedByName = (by: string | undefined, appContext: IAppContext): string => {
  if (!by) return '';
  const user = appContext.globalDataCache.users.items.find((u) => u.email.toLowerCase() === by.toLowerCase());

  return user?.name || by;
};

//The two acceptance commands first on the command bar.
//- notAcceptableReason: set when the selected finding is one a user cannot accept (the fix is elsewhere); Accept is
//  then disabled and its tooltip says why.
export const getAcceptanceCommandBarItems = (
  t: TFunction<string[]>,
  selectedKind: AzureRowKind | undefined,
  canAcceptMore: boolean,
  onAccept: () => void,
  onRemove: () => void,
  notAcceptableReason?: string,
): ICommandBarItemProps[] => {
  const isSelectedFinding = selectedKind === AzureRowKind.Finding;
  const getAcceptTitle = (): string | undefined => {
    if (isSelectedFinding && notAcceptableReason) return notAcceptableReason;
    if (!canAcceptMore) return t('integrations:MS_Azure.Results.CommandBar.MaxReached', { max: MaxAcceptances });

    return undefined;
  };

  const items: ICommandBarItemProps[] = [];

  items.push({
    key: 'accept',
    text: t('integrations:MS_Azure.Results.CommandBar.AcceptUntil'),
    iconProps: acceptIcon,
    disabled: !isSelectedFinding || !canAcceptMore || notAcceptableReason !== undefined,
    title: getAcceptTitle(),
    onClick: onAccept,
  });

  items.push({
    key: 'remove',
    text: t('integrations:MS_Azure.Results.CommandBar.RemoveAcceptance'),
    iconProps: deleteIcon,
    disabled: selectedKind !== AzureRowKind.Accepted,
    onClick: onRemove,
  });

  return items;
};
